'use client';

import { useEffect, useState } from 'react';
import { useTradeContext } from '../TradePageClient/TradePageClient';
import styles from './SelectableQuantityCounter.module.scss';

export type SelectableQuantityCounterProps = {
  initialQuantity?: number;
  acquiredCount?: number;
  goodId: string;
  disabled?: boolean;
  tradeableInCount?: number; // ラベル全体の最大選択可能数
  totalSelectedInLabel?: number; // 同一ラベル内の選択合計数
  isPastDeadline?: boolean; // 期日超過フラグ
  isAutoInput?: boolean; // 自動入力されたかどうかを識別するフラグ
};

function SelectableQuantityCounter({
  initialQuantity = 0,
  acquiredCount,
  goodId,
  disabled = false,
  tradeableInCount,
  totalSelectedInLabel,
  isPastDeadline = false,
  isAutoInput = false,
}: SelectableQuantityCounterProps) {
  const { setSelectedItems } = useTradeContext();
  const [quantity, setQuantity] = useState(initialQuantity);

  // 同一ラベル内の選択合計数（外部から渡される値を使用）
  const labelTotalSelected = totalSelectedInLabel || 0;

  useEffect(() => {
    setSelectedItems((prevItems) => {
      const newMap = new Map(prevItems);
      newMap.set(goodId, quantity);
      return newMap;
    });
  }, [quantity, goodId, setSelectedItems]);

  const handleIncrease = () => {
    setQuantity((prevQuantity) => prevQuantity + 1);
  };

  const handleDecrease = () => {
    if (quantity > 0) {
      setQuantity((prevQuantity) => prevQuantity - 1);
    }
  };

  // 減算ボタンの無効化判定
  const isDecreaseDisabled = () => {
    if (disabled) return true;
    if (quantity <= 0) return true;
    if (isPastDeadline) return true;
    return false;
  };

  // 加算ボタンの無効化判定
  const isIncreaseDisabled = () => {
    if (disabled) return true;
    if (isPastDeadline) return true;
    if (tradeableInCount !== undefined && labelTotalSelected >= tradeableInCount) return true;
    if (acquiredCount !== undefined && quantity >= acquiredCount) return true;
    return false;
  };

  // 数値がいじれないかどうかを判定する関数
  const isQuantityInvalid = () => {
    if (disabled) return true;
    if (isPastDeadline) return true;
    if (isAutoInput) return true;
    return false;
  };

  // 数値がいじれない場合のスタイルを取得する関数
  const getInvalidQuantityClassName = () => {
    // 自動入力の場合は優先的にautoInputスタイルを適用する
    if (isAutoInput) return styles.autoInput;
    // disabledの場合は期限切れと同じスタイルにする
    if (disabled) return styles.pastDeadline;
    if (isPastDeadline) return styles.pastDeadline;
    return '';
  };

  // 数値がいじれる場合のスタイルを取得する関数
  const getValidQuantityClassName = () => {
    if (quantity > 0) return styles.quantityPositive;
    return styles.quantityNotPositive;
  };

  return (
    <div className={styles.container}>
      {!isQuantityInvalid() && (
        <button
          type='button'
          className={`${styles.button} ${styles.decrease}`}
          onClick={handleDecrease}
          disabled={isDecreaseDisabled()}
        >
          -
        </button>
      )}

      {isQuantityInvalid() ? (
        <div className={`${styles.quantityInvalid} ${getInvalidQuantityClassName()}`}>{quantity}</div>
      ) : (
        <div className={`${styles.quantityValid} ${getValidQuantityClassName()}`}>{quantity}</div>
      )}

      {!isQuantityInvalid() && (
        <button
          type='button'
          className={`${styles.button} ${styles.increase}`}
          onClick={handleIncrease}
          disabled={isIncreaseDisabled()}
        >
          +
        </button>
      )}
    </div>
  );
}

export default SelectableQuantityCounter;
